import React from 'react';
import { BreakdownItem } from '../../api/analytics';

interface DonutChartProps {
  data: BreakdownItem[];
  title?: string;
  size?: number;
  thickness?: number;
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  size = 140,
  thickness = 14,
}) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const validData = data.filter((item) => item.count > 0);
  const total = validData.reduce((acc, curr) => acc + curr.count, 0);

  let accumulatedPercent = 0;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex flex-shrink-0 items-center justify-center">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90 transform"
        >
          {/* Trilha de fundo */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke="currentColor"
            strokeWidth={thickness}
            className="text-surface-border"
          />

          {/* Segmentos de rosca */}
          {total > 0 &&
            validData.map((item) => {
              const strokeDasharray = `${(item.percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = -(
                (accumulatedPercent / 100) *
                circumference
              );
              accumulatedPercent += item.percentage;

              return (
                <circle
                  key={item.name}
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="transparent"
                  stroke={item.color}
                  strokeWidth={thickness}
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-300"
                />
              );
            })}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="font-mono text-sm font-bold text-content-main">
            {total}
          </span>
          <span className="text-2xs text-content-muted">
            {total === 1 ? 'clique' : 'cliques'}
          </span>
        </div>
      </div>

      {/* Legenda Lateral */}
      <div className="flex-1 space-y-1.5 overflow-hidden">
        {title && (
          <h4 className="mb-1 text-micro font-semibold uppercase tracking-wider text-content-muted">
            {title}
          </h4>
        )}
        {data.length === 0 || total === 0 ? (
          <div className="py-2 text-xs text-content-muted">
            Nenhum clique registrado no período.
          </div>
        ) : (
          data.map((item) => (
            <div
              key={item.name}
              className="flex items-center justify-between gap-2 text-xs"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2 w-2 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="truncate text-content-main">{item.name}</span>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1.5 font-mono text-micro">
                <span className="font-medium text-content-main">
                  {item.count}
                </span>
                <span className="text-content-muted">({item.percentage}%)</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
